import { IBoxFieldString, IBoxFieldDate, IBoxFieldCurrency, isFieldCurrency } from '../Tender/types';
import { TenderBox } from '..';
import { User } from 'src/models/users';
import { IBidIdentifier } from '../../bids/Bids/types';
import { filterBoxes, convertApiLotsToLots } from '../Tender/helpers';
import { ProcurementStatus } from '../types';
import { IUserName } from 'src/models/users/types';
import { ApiTendersItem, TendersItem } from '../Tenders/types';
import { MpKeyword } from 'src/models/matchingProfiles/types';
import { isNotUndefined } from 'src/helpers';
import { findMatchedKeywords } from '../Preview/helpers';
import { useTendersTable, useTendersBoard, useTendersTimeline } from './hooks';

export class TendersBaseItem {
  readonly id: string;
  readonly name: string;
  readonly buyerName?: string;
  readonly isCommented: boolean;
  readonly isRead: boolean;
  readonly isRejected: boolean;
  readonly nextId?: string;
  readonly bid?: IBidIdentifier;

  constructor(readonly apiTender: TendersItem<ApiTendersItem>) {
    const general = apiTender.general;
    const lots = convertApiLotsToLots(apiTender.lots);
    const nameBox = general.name ? new TenderBox(general.name) : undefined;
    const buyerBox = !!filterBoxes(general.buyerBoxes, lots).length
      ? filterBoxes(general.buyerBoxes, lots)[0]
      : undefined;

    (this.id = apiTender.id),
      (this.name = (nameBox && nameBox.firstField && (nameBox.firstField as IBoxFieldString).string) ?? ''),
      (this.buyerName =
        (buyerBox && buyerBox.firstField && (buyerBox?.firstField as IBoxFieldString).string) ?? undefined),
      (this.isCommented = apiTender.isCommented),
      (this.isRead = apiTender.isRead),
      (this.isRejected = !!apiTender.rejected),
      (this.nextId = apiTender.nextId),
      (this.bid = this.toBid());
  }

  private toBid(): IBidIdentifier | undefined {
    return !!this.apiTender.bids && !!this.apiTender.bids.length
      ? { id: this.apiTender.bids[0].id, wsId: this.apiTender.bids[0].workspace.id }
      : undefined;
  }
}

export class TendersTableItem extends TendersBaseItem {
  readonly published: number | null;
  readonly deadline: number | null;
  readonly assignedTo: IUserName | null;
  readonly isStarred: boolean;
  readonly isFilledData: boolean;
  readonly isInProgress: boolean;
  readonly procurementStatus: ProcurementStatus[];

  constructor(readonly apiTender: TendersItem<ApiTendersItem>) {
    super(apiTender);
    const timeline = apiTender.timeline;
    const lots = convertApiLotsToLots(apiTender.lots);
    const publishedBox = timeline?.availableDate ? new TenderBox(timeline.availableDate) : undefined;
    const deadlineBox = !!filterBoxes(timeline.deadline, lots).length
      ? filterBoxes(timeline.deadline, lots)[0]
      : undefined;

    (this.published =
      (publishedBox && publishedBox.firstField && (publishedBox.firstField as IBoxFieldDate).date) ?? null),
      (this.deadline =
        (deadlineBox && deadlineBox.firstField && (deadlineBox.firstField as IBoxFieldDate).date) ?? null),
      (this.assignedTo = apiTender.assignedTo ? new User(apiTender.assignedTo) : null),
      (this.isStarred = apiTender.isStarred),
      (this.isFilledData = apiTender.isFilledData),
      (this.isInProgress = apiTender.isInProgress),
      (this.procurementStatus = apiTender.procurementStatus);
  }
}

export class TendersBoardItem extends TendersTableItem {
  readonly estValue: IBoxFieldCurrency | null;
  readonly keywords?: MpKeyword[];

  constructor(apiTender: TendersItem<ApiTendersItem>, private readonly _keywords?: MpKeyword[]) {
    super(apiTender);

    (this.estValue = this.toEstValue()), (this.keywords = this.toKeywords());
  }

  private toEstValue(): IBoxFieldCurrency | null {
    const lots = convertApiLotsToLots(this.apiTender.lots);
    const estimationValueBox = filterBoxes(this.apiTender.general.contractValueBoxes, lots).length
      ? filterBoxes(this.apiTender.general.contractValueBoxes, lots)[0]
      : undefined;
    return estimationValueBox && estimationValueBox.firstField && isFieldCurrency(estimationValueBox.firstField)
      ? estimationValueBox.firstField
      : null;
  }

  private toKeywords(): MpKeyword[] | undefined {
    return this._keywords ? findMatchedKeywords(this.apiTender.highlights, this._keywords) : undefined;
  }
}

export class TendersTimelineItem extends TendersBaseItem {
  readonly estValue: IBoxFieldCurrency | null;
  readonly title: string;
  readonly isLots: boolean;
  readonly dates: number[];

  constructor(apiTender: TendersItem<ApiTendersItem>) {
    super(apiTender);
    const { contractEnd, contractStart, extensionEndDates } = apiTender.expiringContract;
    const { general } = apiTender;

    this.title = this.name;
    this.isLots = general.contractDurationBoxes.some(box => !!box.lots?.length);
    this.dates = [...new Set([contractStart, contractEnd, ...(extensionEndDates ?? [])])].filter(isNotUndefined);
    this.estValue = this.toEstValue();
  }

  private toEstValue(): IBoxFieldCurrency | null {
    const lots = convertApiLotsToLots(this.apiTender.lots);
    const estimationValueBox = !filterBoxes(this.apiTender.general.contractValueBoxes, lots).length
      ? filterBoxes(this.apiTender.general.contractValueBoxes, lots)[0]
      : undefined;
    return estimationValueBox && estimationValueBox.firstField && isFieldCurrency(estimationValueBox.firstField)
      ? estimationValueBox.firstField
      : null;
  }
}

export { useTendersTable, useTendersBoard, useTendersTimeline };
